import React, { useState } from 'react'
import { CloseRounded } from '@mui/icons-material'
import { Autocomplete, Chip, FormHelperText, TextField } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'

import { color } from '../../theme'

interface EmailSelectProps {
  onChange: (value: any) => void
  selected: string[]
  maxHeight?: number
  style?: React.CSSProperties
  helperText?: React.ReactNode
  error?: boolean
  maxEmails?: number
}

export const EmailSelect = (props: EmailSelectProps) => {
  const { onChange, maxHeight, selected, style, helperText, error = false, maxEmails } = props

  const atLimit = maxEmails != null && selected.length >= maxEmails

  const useStyles = makeStyles({
    paper: { height: 0 },
    input: {
      '&::placeholder': {
        color: color.palette.darkGrey,
        fontFamily: 'Inter',
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: 14,
      },
      '& .MuiInputBase-root': {
        height: maxHeight ?? 175,
        overflow: 'auto',
        border: '1px solid lightgray',
        alignContent: 'flex-start',
      },
      '& .MuiInputBase-root::focused': {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      },
      '& .MuiInputBase-root > fieldset': {
        border: 'none',
      },
    },
  })

  const classes = useStyles()

  const renderTags = (value: string[], getTagProps) => {
    return value.map((option: string, index: number) => (
      <Chip
        sx={{
          ...CHIP_CONTAINER,
          border: `1px solid ${!option.includes('@') ? `${color.red} ` : '#EFF0F6'} !important`,
          color: !option.includes('@') ? `${color.red}` : '#212121',
        }}
        deleteIcon={
          <CloseRounded
            style={{ fontSize: 18, color: !option.includes('@') ? `${color.red}` : '#9098A3' }}
          />
        }
        label={option}
        {...getTagProps({ index })}
      />
    ))
  }

  const [ival, setIval] = useState('')

  const handlePaste = (e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text')
    if (!text.includes(',') && !text.includes(' ') && !text.includes('\n')) return
    e.preventDefault()
    let newEmails = text
      .split(/[,\s\n]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0 && !selected.includes(s))
    if (maxEmails != null) {
      const slotsLeft = maxEmails - selected.length
      newEmails = newEmails.slice(0, Math.max(0, slotsLeft))
    }
    if (newEmails.length > 0) {
      onChange([...selected, ...newEmails])
      setIval('')
    }
  }

  const handleKeyDown = (e) => {
    switch (e.key) {
      case ',':
      case 'Enter':
      case ' ': {
        e.preventDefault()
        e.stopPropagation()
        if (e.target.value.length > 0 && !selected.includes(e.target.value) && !atLimit) {
          onChange([...selected, e.target.value])
          setIval('')
        }
        break
      }
      default:
    }
  }

  return (
    <>
      <Autocomplete
        classes={{ ...classes }}
        onBlur={(e: any) => {
          if (e.target.value.length > 0 && !selected.includes(e.target.value) && !atLimit) {
            onChange([...selected, e.target.value])
            setIval('')
          }
        }}
        style={style}
        multiple
        options={[]}
        clearOnBlur={true}
        blurOnSelect={true}
        value={selected}
        inputValue={ival}
        freeSolo
        disableClearable
        filterSelectedOptions
        renderTags={renderTags}
        onInputChange={(e: any) => setIval(e.target.value)}
        onChange={(event, newValue) => onChange(newValue)}
        disabled={atLimit && selected.length === 0}
        renderInput={(params) => {
          params.inputProps.onKeyDown = handleKeyDown
          params.inputProps.onPaste = handlePaste
          return (
            <TextField
              {...params}
              className={classes.input}
              disabled={atLimit}
              placeholder={
                atLimit
                  ? 'Maximum number of users reached'
                  : selected.length === 0
                    ? 'Copy paste email addresses of existing users separated by commas / spaces and then press "Enter"'
                    : ''
              }
            />
          )
        }}
      />
      {helperText != null && (
        <FormHelperText error={error} sx={{ mx: '14px', mt: '3px' }}>
          {helperText}
        </FormHelperText>
      )}
    </>
  )
}

const CHIP_CONTAINER: React.CSSProperties = {
  backgroundColor: '#EFF0F6',
  borderRadius: '8px !important',
  padding: 0,
}
